import { position } from "../../../base/browser/dom";
import { disposableTimeout } from "../../../base/common/async";
import { Emitter, Event } from "../../../base/common/events";
import { Disposable, IDisposable } from "../../../base/common/lifecycle";
import { INotification, INotificationService, NotificationCoordinates, NotificationOptions, NotificationType, ShowNotificationOptions } from "./notifications";

const defaultCloseNotificationDelay = 10_000;

export class NotificationService extends Disposable implements INotificationService {

    _serviceBrand: undefined;

    constructor() {
        super();
    }

    dispose(): void {
        super.dispose();
        this._notifications.forEach(n => n.dispose());
        this._notifications.length = 0;
    }

    private readonly _onDidChange = this._register(new Emitter<void>());
    readonly onDidChange = this._onDidChange.event;

    private readonly _notifications: StandardNotification[] = [];
    private readonly _dismissTimeouts: Map<string, IDisposable> = new Map();

    get notifications(): readonly INotification[] {
        return this._notifications;
    }

    success(message: string, opts?: NotificationOptions): IDisposable {
        return this.showNotification({ ...opts, message, type: 'success' });
    }
    info(message: string, opts?: NotificationOptions): IDisposable {
        return this.showNotification({ ...opts, message, type: 'info' });
    }
    warn(message: string, opts?: NotificationOptions): IDisposable {
        return this.showNotification({ ...opts, message, type: 'warning' });
    }
    error(message: string, opts?: NotificationOptions): IDisposable {
        return this.showNotification({ ...opts, message, type: 'error' });
    }

    showNotification(options: ShowNotificationOptions): IDisposable {

        const existing = options.key ? this._notifications.find(n => n.key === options.key) : undefined;
        if (existing) {
            const timeout = this._dismissTimeouts.get(existing.id);
            timeout?.dispose();
            existing.message = options.message;
            existing.type = options.type || 'info';
            this._dismissTimeouts.set(existing.id, disposableTimeout(() => this.dismissNotification(existing.id), options.duration || defaultCloseNotificationDelay));
            return existing;
        }

        if (options.positionXY) {
            options.position = "xy";
        }
        else if (!options.position) {
            options.position = "toast";
        }

        const notification = new StandardNotification(options, {
            onClick: options.onClick,
            dismiss: () => this.dismissNotification(notification.id)
        });
        this._dismissTimeouts.set(notification.id, disposableTimeout(() => this.dismissNotification(notification.id), options.duration || defaultCloseNotificationDelay));

        this._notifications.push(notification);
        this._onDidChange.fire();
        return notification;
    }

    dismissNotification(id: string): void {
        const index = this._notifications.findIndex(n => n.id === id);
        if (index !== -1) {
            const notification = this._notifications[index];
            if (notification.closing === true) return;
            notification.closing = true;
            setTimeout(() => {
                const index = this._notifications.findIndex(n => n.id === id);
                if (index === -1) return;
                this._notifications.splice(index, 1);
                this._onDidChange.fire();
            }, 2000);
        }
    }
}

class StandardNotification implements INotification {

    constructor(options: ShowNotificationOptions, handler: {
        onClick?: () => void,
        dismiss: () => void,
    }) {
        this.id = Math.random().toString(36).substring(2, 9);
        this.key = options.key;
        this.message = options.message;

        this.type = options.type || "info";
        this.icon = options.icon;
        this.duration = options.duration;
        this.position = options.position;
        this.positionXY = options.positionXY;

        const onClick = handler.onClick;
        const dismiss = handler.dismiss;

        if (onClick) {
            this.onClick = () => {
                onClick();
            };
        }
        if (dismiss) {
            this.dismiss = () => {
                dismiss();
            };
        }
    }


    private readonly _onDidChange = new Emitter<void>();
    readonly onDidChange = this._onDidChange.event;

    readonly id: string;
    readonly key?: string | undefined;

    set message(value: string) {
        if (value !== this._message) {
            this._message = value;
            this._onDidChange.fire();
        }
    }
    get message(): string {
        return this._message;
    }
    private _message: string;

    get type() {
        return this._type;
    }
    set type(value: NotificationType) {
        if (value !== this._type) {
            this._type = value;
            this._onDidChange.fire();
        }
    }
    private _type: NotificationType;

    private _closing: boolean = false;
    get closing() {
        return this._closing;
    }
    set closing(value: boolean) {
        if (value === this._closing) return;
        this._closing = value;
        this._onDidChange.fire();
    }

    icon?: string | undefined;

    duration?: number | undefined;

    position?: INotification["position"] | undefined;

    positionXY?: NotificationCoordinates | undefined;

    onClick?: (() => void) | undefined;

    dismiss?: (() => void) | undefined;

    dispose(): void {
        this.dismiss?.();
     }

}